import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {
  Aangifte, BokkzAlgDataService,
  IncomeTaxEntity,
  LoonUitTegenwoordigeDienstbetrekking,
  OmschrijvingEnBedrag
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzIbDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import {BehaviorSubject, filter, Subject, takeUntil} from "rxjs";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {Event, NavigationEnd, Router} from "@angular/router";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-loon-tegenwoordig',
  templateUrl: './loon-tegenwoordig.component.html',
  styleUrls: ['./loon-tegenwoordig.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class LoonTegenwoordigComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Input() partner = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();
  private isSaving = false;
  private focusedElementId: string | null = null;
  public clientId!: string;
  public aangifte = {} as Aangifte;
  public incomeTaxEntity = {} as IncomeTaxEntity;
  public partnerBoolean = {} as boolean;
  public wajongFg = {} as FormGroup;
  public loonTegenwoordigFg = {} as FormGroup;
  public isDefAangifte = {} as boolean;

  public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
    {value: true, label: "Ja, wel wajong-korting"},
    {value: false, label: "Nee, geen wajong-korting"},
  ];

  constructor(public dataService: BokkzIbDataService,
              public dataAlgService: BokkzAlgDataService,
              private router: Router,
              private fb: FormBuilder) {
    this.loonTegenwoordigFg = this.fb.group({
      loonTegenwoordigeArbeid: this.fb.array<LoonUitTegenwoordigeDienstbetrekking>([], Validators.required)
    })
    this.wajongFg = this.fb.group({
      krijgtWajong: null as unknown as boolean
    })
  }

  public ngOnInit(): void {
    this.dataService.isDefAangifte.pipe(takeUntil(this.destroy$)).subscribe((isDefAangifte) => {
      this.isDefAangifte = isDefAangifte;
    });
    this.dataService.incomeTaxEntity.pipe(takeUntil(this.destroy$)).subscribe((incomeTaxEntity) => {
      this.incomeTaxEntity = incomeTaxEntity;
      if (incomeTaxEntity.aangifte) {
        this.updateForm();
        this.partner.subscribe((partner) => {
          this.partnerBoolean = partner
          this.loonTegenwoordigFg.reset()
          this.wajongFg.reset()
          this.updateForm();
        });
      }
    });
    this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
      this.clientId = client.sstRelationId!;
    });
    this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.handleUrlChange();
      });
  }

  ngOnDestroy(): void {
    if (this.destroy$) {
      this.destroy$.unsubscribe();
    }
  }

  get loonTegenwoordigeArbeid(): FormArray {
    return this.loonTegenwoordigFg.get('loonTegenwoordigeArbeid') as FormArray;
  }

  addRij(): void {
    this.loonTegenwoordigeArbeid.push(this.createFormGroup())
  }

  verwijderRij(index: number): void {
    this.loonTegenwoordigeArbeid.removeAt(index);
    this.loonTegenwoordigFg.updateValueAndValidity()
    this.loonTegenwoordigFg.markAsDirty()
    this.saveForm()
  }

  createFormGroup(loon?: LoonUitTegenwoordigeDienstbetrekking): FormGroup {
    return this.fb.group({
      loon: this.fb.group<OmschrijvingEnBedrag>({
        omschrijving: loon ? loon.loon?.omschrijving : null as unknown as string,
        bedrag: loon ? loon.loon?.bedrag : null as unknown as number,
      }),
      loonheffing: loon ? loon.loonheffing : null as unknown as number,
      arbeidskorting: loon ? loon.arbeidskorting : null as unknown as number,
      onbelastDeelLoonInNederland: loon ? loon.onbelastDeelLoonInNederland : null as unknown as number,
      loonsoort: loon ? loon.loonsoort : null as unknown as number,
    })
  }

  handleUrlChange() {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      if (!this.isDefAangifte) {
        this.dataService.postIncomeTaxEntityMetLoonheffing(this.clientId, false, this.incomeTaxEntity);
      } else {
        this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
      }
      this.isSaving = false;
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.loonTegenwoordigFg.dirty;
  }

  private updateForm(): void {
    let loonUitTegenwoordigeDienstbetrekkingLijst = this.incomeTaxEntity.aangifte?.box1?.belastbaarLoon?.loonUitTegenwoordigeDienstbetrekking || [];
    let wajong = this.incomeTaxEntity.aangifte?.heffingskortingen ? this.incomeTaxEntity.aangifte?.heffingskortingen?.krijgtWajong : false;
    this.wajongFg.patchValue({krijgtWajong: wajong});

    this.partner.subscribe((partner) => {
      this.partnerBoolean = partner;
      if (partner) {
        loonUitTegenwoordigeDienstbetrekkingLijst = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.belastbaarLoon?.loonUitTegenwoordigeDienstbetrekking || [];
        wajong = this.incomeTaxEntity.aangifte?.partnerAangifte?.heffingskortingen ? this.incomeTaxEntity.aangifte?.partnerAangifte?.heffingskortingen?.krijgtWajong : false;
        this.wajongFg.patchValue({krijgtWajong: wajong});
      }
    });

    const formArray = this.loonTegenwoordigeArbeid;
    const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

    while (formArray.length > loonUitTegenwoordigeDienstbetrekkingLijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < loonUitTegenwoordigeDienstbetrekkingLijst.length; i++) {
      if (i < formArray.length) {
        const formGroup = formArray.at(i) as FormGroup;
        formGroup.patchValue(loonUitTegenwoordigeDienstbetrekkingLijst[i]);
      } else {
        const formGroup = this.createFormGroup(loonUitTegenwoordigeDienstbetrekkingLijst[i]);
        formArray.push(formGroup);
      }
    }

    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }
  }

  saveForm(): void {
    this.loonTegenwoordigFg.updateValueAndValidity()
    this.wajongFg.updateValueAndValidity()
    if (!this.partnerBoolean) {
      this.incomeTaxEntity.aangifte = {
        ...this.incomeTaxEntity.aangifte,
        box1: {
          ...this.incomeTaxEntity.aangifte?.box1,
          belastbaarLoon: {
            ...this.incomeTaxEntity.aangifte?.box1?.belastbaarLoon,
            loonUitTegenwoordigeDienstbetrekking: this.loonTegenwoordigFg.value.loonTegenwoordigeArbeid
          }
        },
        heffingskortingen: {
          krijgtWajong: this.wajongFg.get('krijgtWajong')?.value ? this.wajongFg.get('krijgtWajong')?.value : false,
        }
      } as Aangifte;
      this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
    } else {
      this.incomeTaxEntity.aangifte = {
        ...this.incomeTaxEntity.aangifte,
        partnerAangifte: {
          ...this.incomeTaxEntity.aangifte?.partnerAangifte,
          box1: {
            ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1,
            belastbaarLoon: {
              ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.belastbaarLoon,
              loonUitTegenwoordigeDienstbetrekking: this.loonTegenwoordigFg.value.loonTegenwoordigeArbeid,
            }
          },
          heffingskortingen: {
            krijgtWajong: this.wajongFg.get('krijgtWajong')?.value ? this.wajongFg.get('krijgtWajong')?.value : false,
          }
        },
      } as Aangifte;
      this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
    }
    this.loonTegenwoordigFg.markAsDirty()
    this.wajongFg.markAsDirty()
  }

  heeftGeenOuderenkorting(): boolean {
    const aangifte = this.incomeTaxEntity.aangifte!;
    let geboorteDatum = new Date();

    if (this.partnerBoolean && aangifte?.partnerAangifte) {
      // Gebruik de geboortedatum van de partner als partnerBoolean waar is
      geboorteDatum = new Date(aangifte.partnerAangifte.aangever?.geboortedatum);
    } else if (aangifte?.aangever?.geboortedatum) {
      // Anders, gebruik de geboortedatum van de aangever
      geboorteDatum = new Date(aangifte.aangever.geboortedatum);
    }

    const aangifteJaar = this.aangifte.aangifteJaar!;
    const AOWdatum = new Date(aangifteJaar - 67, 11, 31);

    // Vergelijk alleen jaar, maand en dag
    return (
      geboorteDatum.getFullYear() > AOWdatum.getFullYear() ||
      (geboorteDatum.getFullYear() === AOWdatum.getFullYear() &&
        geboorteDatum.getMonth() > AOWdatum.getMonth()) ||
      (geboorteDatum.getFullYear() === AOWdatum.getFullYear() &&
        geboorteDatum.getMonth() === AOWdatum.getMonth() &&
        geboorteDatum.getDate() > AOWdatum.getDate())
    );
  }
}
